import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../config';
import {
  GROUP_READS_COLLECTION,
  UNREAD_FETCH_LIMIT,
  countUnread,
  groupReadDocId,
  resolveLastReadMs,
  toMillis,
} from '@/lib/groups/groupUnread';

/**
 * Server-side group chat read state (namat#36 P2): groupReads/{uid}_{groupId}.
 * Mobile writes and reads the same doc, so a chat read on one device clears
 * its badge everywhere.
 */
export async function markGroupRead(userId: string, groupId: string): Promise<void> {
  if (!userId || !groupId) return;
  try {
    await setDoc(
      doc(db, GROUP_READS_COLLECTION, groupReadDocId(userId, groupId)),
      { userId, groupId, lastReadAt: serverTimestamp() },
      { merge: true },
    );
  } catch (error) {
    console.error('Error marking group as read:', error);
  }
}

/**
 * Live unread count for one group: follows the member's read doc and, for its
 * current lastReadAt, at most UNREAD_FETCH_LIMIT newer messages (served by the
 * groupMessages groupId+createdAt index).
 */
export function subscribeToGroupUnread(
  userId: string,
  groupId: string,
  onCount: (count: number) => void,
): () => void {
  let unsubMessages: (() => void) | null = null;
  let currentLastRead: number | null | undefined;
  let baselineWritten = false;

  const listenToMessages = (lastReadMs: number | null) => {
    unsubMessages?.();
    const constraints = [
      where('groupId', '==', groupId),
      ...(lastReadMs !== null ? [where('createdAt', '>', Timestamp.fromMillis(lastReadMs))] : []),
      orderBy('createdAt', 'asc'),
      limit(UNREAD_FETCH_LIMIT),
    ];
    unsubMessages = onSnapshot(
      query(collection(db, 'groupMessages'), ...constraints),
      (snapshot) => {
        onCount(countUnread(snapshot.docs.map((d) => d.data()), userId, lastReadMs));
      },
      (error) => console.error('Error in unread subscription:', error),
    );
  };

  const unsubRead = onSnapshot(
    doc(db, GROUP_READS_COLLECTION, groupReadDocId(userId, groupId)),
    (snap) => {
      // 'estimate' so our own just-written serverTimestamp() reads as "now"
      // instead of null while it is pending.
      let lastReadMs: number | null;
      if (snap.exists()) {
        lastReadMs = toMillis(snap.data({ serverTimestamps: 'estimate' })?.lastReadAt);
      } else {
        // First sight: the group counts as read up to now and the doc is
        // written as that baseline, so only later messages are unread.
        lastReadMs = resolveLastReadMs(null, null, Date.now());
        if (!baselineWritten) {
          baselineWritten = true;
          void markGroupRead(userId, groupId);
        }
      }
      if (lastReadMs === currentLastRead) return;
      currentLastRead = lastReadMs;
      listenToMessages(lastReadMs);
    },
    (error) => {
      console.error('Error in group read subscription:', error);
      if (currentLastRead === undefined) {
        currentLastRead = Date.now();
        listenToMessages(currentLastRead);
      }
    },
  );

  return () => {
    unsubRead();
    unsubMessages?.();
  };
}

/** Live unread counts for several groups, keyed by group id. */
export function subscribeToUnreadCounts(
  groupIds: string[],
  userId: string,
  onUpdate: (counts: Record<string, number>) => void,
): () => void {
  if (!userId || groupIds.length === 0) {
    onUpdate({});
    return () => {};
  }
  const counts: Record<string, number> = {};
  const unsubs = groupIds.map((groupId) =>
    subscribeToGroupUnread(userId, groupId, (count) => {
      counts[groupId] = count;
      onUpdate({ ...counts });
    }),
  );
  return () => unsubs.forEach((u) => u());
}
